import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios'
import { isControlPlaneEnabled } from './config'
import { ensureFreshL3Token, refreshControlPlaneSession } from './refresh'
import { clearL3Token } from './tokenStore'

/**
 * CP bearer attach for the admin console's L3 API calls (Account Control Plane
 * P5). When CP auth is enabled, every request through the given axios instance
 * carries the CP-minted L3 token, and a 401 triggers one forced rotation +
 * retry. With CP disabled the interceptors are inert, so behavior is
 * byte-identical to the legacy session path.
 *
 * The instance is expected to be bearer-only under CP (see `main.tsx`, which
 * sets `withCredentials = false`): sending the stale legacy `connect.sid` would
 * let the L3 server's dark-mode session branch win over the bearer.
 */
type RetriableConfig = AxiosRequestConfig & { __cpAuthRetry?: boolean }

function hasAuthHeader(headers: AxiosRequestConfig['headers']): boolean {
  if (!headers) return false
  if ('has' in headers && typeof headers.has === 'function') {
    return headers.has('Authorization')
  }
  return Object.keys(headers).some(key => key.toLowerCase() === 'authorization')
}

function setAuthHeader(config: AxiosRequestConfig, token: string): void {
  const headers = config.headers
  if (headers && 'set' in headers && typeof headers.set === 'function') {
    headers.set('Authorization', `Bearer ${token}`)
    return
  }
  config.headers = { ...headers, Authorization: `Bearer ${token}` }
}

export function installControlPlaneBearer(instance: AxiosInstance): void {
  instance.interceptors.request.use(async config => {
    if (isControlPlaneEnabled() && !hasAuthHeader(config.headers)) {
      const token = await ensureFreshL3Token()
      if (token) setAuthHeader(config, token)
    }
    return config as InternalAxiosRequestConfig
  })

  instance.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined
    if (isControlPlaneEnabled() && error.response?.status === 401 && config && !config.__cpAuthRetry) {
      // The token appeared fresh but the server rejected it (revoked/skew) —
      // force one rotation, then retry the request exactly once.
      clearL3Token()
      await refreshControlPlaneSession().catch(() => null)
      const token = await ensureFreshL3Token()
      if (token) {
        config.__cpAuthRetry = true
        setAuthHeader(config, token)
        return instance.request(config)
      }
    }
    return Promise.reject(error)
  })
}
